import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FiSearch,
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiImage,
  FiUpload,
  FiCalendar,
  FiArrowUp,
  FiArrowDown,
  FiX,
  FiGrid,
  FiSliders,
  FiRefreshCw,
  FiChevronDown,
  FiCheck,
} from "react-icons/fi";
import { FaImages, FaRegImage, FaImage } from "react-icons/fa";

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [projects, setProjects] = useState([]); // New state for projects
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false); // For project dropdown
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    banner_image: null,
    project_id: "",
    status: "active",
  });
  // https://mithahomes.com/backend/api/testbanner/get-banners/get
  // For development - change this to your actual API base URL
  const API_BASE = " https://mithahomes.com/backend/api/testbanner";
  const PROJECTS_API =
    "https://mithahomes.com/backend/api/mainprojectprocess/projects/sampletes";

  // Statistics
  const stats = {
    total: banners.length,
    active: banners.filter((b) => b.status === "active").length,
    inactive: banners.filter((b) => b.status === "inactive").length,
  };

  // Fetch all banners
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/get-banners/get`);
      if (response.data.success && response.data.data) {
        setBanners(response.data.data);
      } else {
        throw new Error(response.data.message || "Failed to fetch banners");
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      toast.error("❌ Error loading banners");
      // For development, use mock data
      setBanners(mockBanners);
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects for dropdown
  const fetchProjects = async () => {
    try {
      const response = await axios.get(PROJECTS_API);
      if (response.data.success && response.data.projects) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("❌ Error loading projects");
    }
  };

  useEffect(() => {
    fetchBanners();
    fetchProjects();
  }, []);

  // Mock data for development
  const mockBanners = [
    {
      id: 1,
      banner_image: "/uploads/banner1.jpg",
      project_id: 84,
      status: "active",
      created_at: "2024-01-15T10:30:00Z",
    },
    {
      id: 2,
      banner_image: "/uploads/banner2.jpg",
      project_id: 78,
      status: "inactive",
      created_at: "2024-01-14T14:45:00Z",
    },
    {
      id: 3,
      banner_image: "/uploads/banner3.jpg",
      project_id: null,
      status: "active",
      created_at: "2024-01-13T09:15:00Z",
    },
  ];

  // Get project name by ID
  const getProjectName = (projectId) => {
    if (!projectId) return "No Project";
    const project = projects.find((p) => p.pid === projectId);
    return project ? project.project_name : "Unknown Project";
  };

  // Filter banners
  const filteredBanners = banners.filter((banner) => {
    const matchesStatus =
      filterStatus === "all" || banner.status === filterStatus;
    const matchesSearch =
      banner.banner_image?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getProjectName(banner.project_id)
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Sort banners
  const sortedBanners = React.useMemo(() => {
    if (!sortConfig.key) return filteredBanners;

    return [...filteredBanners].sort((a, b) => {
      // For project_id sorting, sort by project name
      if (sortConfig.key === "project_id") {
        const aName = getProjectName(a.project_id);
        const bName = getProjectName(b.project_id);
        if (aName < bName) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aName > bName) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      }

      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredBanners, sortConfig, projects]);

  // Pagination
  const totalPages = Math.ceil(sortedBanners.length / itemsPerPage);
  const paginatedBanners = sortedBanners.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("❌ Please select a valid image file (JPEG, PNG, GIF)");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("❌ Image size should be less than 5MB");
        return;
      }

      setFormData((prev) => ({ ...prev, banner_image: file }));

      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const resetForm = () => {
    setFormData({
      banner_image: null,
      project_id: "",
      status: "active",
    });
    setEditingBanner(null);
    setImagePreview(null);
    setShowProjectDropdown(false);
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, banner_image: null }));
    setImagePreview(null);
  };

  // Select project
  const selectProject = (projectId) => {
    setFormData((prev) => ({ ...prev, project_id: projectId }));
    setShowProjectDropdown(false);
  };

  // Validate form
  const validateForm = () => {
    if (!editingBanner && !formData.banner_image) {
      toast.error("❌ Please upload a banner image");
      return false;
    }
    return true;
  };

  // Add new banner
  const handleAddBanner = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setUploading(true);
      const submitData = new FormData();

      submitData.append("banner_image", formData.banner_image);
      if (formData.project_id) {
        submitData.append("project_id", formData.project_id);
      }

      const response = await axios.post(
        `${API_BASE}/add-banner/add`,
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        toast.success("✅ Banner added successfully!");
        setShowModal(false);
        resetForm();
        fetchBanners();
      } else {
        throw new Error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error adding banner:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Error adding banner";
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  // Update banner status
  const handleUpdateStatus = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
      const response = await axios.put(
        `${API_BASE}/update-banner-status/${id}`,
        {
          status: newStatus,
        },
      );

      if (response.data.success) {
        toast.success(
          `Banner ${newStatus === "active" ? "activated" : "deactivated"}!`,
        );
        fetchBanners();
      } else {
        throw new Error(response.data.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error updating status";
      toast.error(`❌ ${errorMessage}`);
    }
  };

  // Delete banner
  const handleDeleteBanner = async (id) => {
    if (
      window.confirm(
        "Are you sure you want to delete this banner? This action cannot be undone.",
      )
    ) {
      try {
        const response = await axios.delete(
          `${API_BASE}/delete-banner/del/${id}`,
        );

        if (response.data.success) {
          toast.success("🗑️ Banner deleted successfully!");
          fetchBanners();
        } else {
          throw new Error(response.data.message || "Delete failed");
        }
      } catch (error) {
        console.error("Error deleting banner:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error deleting banner";
        toast.error(`❌ ${errorMessage}`);
      }
    }
  };

  // Edit banner
  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      banner_image: null,
      project_id: banner.project_id || "",
      status: banner.status,
    });
    setShowModal(true);
  };

  // Update banner (new function)
  const handleUpdateBanner = async (e) => {
    e.preventDefault();

    if (!editingBanner) return;

    try {
      setUploading(true);
      const submitData = new FormData();

      if (formData.banner_image) {
        submitData.append("banner_image", formData.banner_image);
      }

      if (formData.project_id !== undefined) {
        submitData.append("project_id", formData.project_id);
      }

      submitData.append("status", formData.status);

      const response = await axios.put(
        `${API_BASE}/edit-banner/${editingBanner.id}`,
        submitData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data.success) {
        toast.success("✅ Banner updated successfully!");
        setShowModal(false);
        resetForm();
        fetchBanners();
      } else {
        throw new Error(response.data.message || "Operation failed");
      }
    } catch (error) {
      console.error("Error updating banner:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Error updating banner";
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    return status === "active"
      ? { background: "#d1fae5", color: "#065f46", border: "#a7f3d0" }
      : { background: "#fef3c7", color: "#92400e", border: "#fde68a" };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get full image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (imagePath.startsWith("http")) return imagePath;
    if (imagePath.startsWith("/")) {
      return ` https://mithahomes.com/backend${imagePath}`;
    }
    return `https://mithahomes.com/backend/uploads/${imagePath}`;
  };

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        background: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "30px",
          flexWrap: "wrap",
          gap: "20px",
        }}
      >
        <div>
          <h1
            style={{
              fontSize: "2.5rem",
              fontWeight: "700",
              color: "#1e293b",
              margin: "0 0 8px 0",
            }}
          >
            Banner Management
          </h1>
          <p
            style={{
              fontSize: "1.1rem",
              color: "#64748b",
              margin: "0",
            }}
          >
            Manage your website banners and promotions
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "12px",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
            transition: "all 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-2px)";
            e.target.style.boxShadow = "0 8px 25px rgba(102, 126, 234, 0.4)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.3)";
          }}
        >
          <FiPlus style={{ fontSize: "1.1rem" }} />
          Add New Banner
        </button>
      </div>

      {/* Statistics Cards - Same as before */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {/* Total Banners - Same as before */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FaImages />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.total}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Total Banners
            </p>
          </div>
        </div>

        {/* Active Banners - Same as before */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #10b981, #059669)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiEye />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.active}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Active Banners
            </p>
          </div>
        </div>

        {/* Inactive Banners - Same as before */}
        <div
          style={{
            background: "white",
            padding: "25px",
            borderRadius: "16px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            display: "flex",
            alignItems: "center",
            gap: "20px",
            transition: "transform 0.3s ease, box-shadow 0.3s ease",
          }}
          onMouseOver={(e) => {
            e.target.style.transform = "translateY(-4px)";
            e.target.style.boxShadow = "0 12px 25px rgba(0, 0, 0, 0.15)";
          }}
          onMouseOut={(e) => {
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 4px 6px -1px rgba(0, 0, 0, 0.1)";
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #ef4444, #dc2626)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              color: "white",
            }}
          >
            <FiEyeOff />
          </div>
          <div>
            <h3
              style={{
                fontSize: "2rem",
                fontWeight: "700",
                color: "#1e293b",
                margin: "0 0 4px 0",
              }}
            >
              {stats.inactive}
            </h3>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#64748b",
                margin: "0",
              }}
            >
              Inactive Banners
            </p>
          </div>
        </div>
      </div>

      {/* Controls Bar - Same as before */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          marginBottom: "25px",
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            position: "relative",
            flex: "1",
            minWidth: "300px",
          }}
        >
          <FiSearch
            style={{
              position: "absolute",
              left: "15px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94a3b8",
              fontSize: "1.1rem",
            }}
          />
          <input
            type="text"
            placeholder="Search banners by filename or project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "12px 20px 12px 45px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              transition: "all 0.3s ease",
            }}
            onFocus={(e) => {
              e.target.style.borderColor = "#667eea";
              e.target.style.boxShadow = "0 0 0 3px rgba(102, 126, 234, 0.1)";
            }}
            onBlur={(e) => {
              e.target.style.borderColor = "#e2e8f0";
              e.target.style.boxShadow = "none";
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
          }}
        >
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={{
              padding: "12px 16px",
              border: "2px solid #e2e8f0",
              borderRadius: "12px",
              fontSize: "1rem",
              background: "white",
              cursor: "pointer",
              minWidth: "140px",
            }}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            onClick={() => {
              fetchBanners();
              fetchProjects();
            }}
            style={{
              background: "transparent",
              border: "2px solid #e2e8f0",
              padding: "12px 16px",
              borderRadius: "12px",
              fontSize: "1rem",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.3s ease",
            }}
            onMouseOver={(e) => {
              e.target.style.background = "#f8fafc";
              e.target.style.borderColor = "#cbd5e1";
            }}
            onMouseOut={(e) => {
              e.target.style.background = "transparent";
              e.target.style.borderColor = "#e2e8f0";
            }}
          >
            <FiRefreshCw />
            Refresh
          </button>
        </div>
      </div>

      {/* Data Table - Updated with project column */}
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
          marginBottom: "30px",
        }}
      >
        {loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "4px solid #f1f5f9",
                borderTop: "4px solid #667eea",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            ></div>
            <p
              style={{
                color: "#64748b",
                fontSize: "1rem",
                margin: "0",
              }}
            >
              Loading banners...
            </p>
          </div>
        ) : sortedBanners.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 40px",
              color: "#64748b",
            }}
          >
            <FaRegImage
              style={{
                fontSize: "4rem",
                color: "#cbd5e1",
                marginBottom: "20px",
              }}
            />
            <h3
              style={{
                fontSize: "1.5rem",
                color: "#475569",
                margin: "0 0 12px 0",
              }}
            >
              No banners found
            </h3>
            <p
              style={{
                fontSize: "1rem",
                margin: "0 0 25px 0",
              }}
            >
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
              }}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "12px",
                fontSize: "1rem",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    width: "50px",
                  }}
                >
                  #
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Banner Image
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("project_id")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Project
                    {sortConfig.key === "project_id" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>
                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSort("status")}
                >
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    Status
                    {sortConfig.key === "status" &&
                      (sortConfig.direction === "asc" ? (
                        <FiArrowUp />
                      ) : (
                        <FiArrowDown />
                      ))}
                  </span>
                </th>

                <th
                  style={{
                    background: "#f8fafc",
                    padding: "16px 20px",
                    textAlign: "left",
                    fontWeight: "600",
                    color: "#475569",
                    borderBottom: "2px solid #e2e8f0",
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedBanners.map((banner, index) => (
                <tr
                  key={banner.id}
                  style={{
                    transition: "background 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = "#f8fafc";
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = "white";
                  }}
                >
                  <td
                    style={{
                      padding: "20px",
                      borderBottom: "1px solid #f1f5f9",
                      color: "#64748b",
                      fontWeight: "500",
                      verticalAlign: "top",
                    }}
                  >
                    {(currentPage - 1) * itemsPerPage + index + 1}
                  </td>

                  <td
                    style={{
                      padding: "20px",
                      borderBottom: "1px solid #f1f5f9",
                      verticalAlign: "top",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "15px",
                      }}
                    >
                      {banner.banner_image ? (
                        <div
                          style={{
                            position: "relative",
                            width: "120px",
                            height: "80px",
                            borderRadius: "8px",
                            overflow: "hidden",
                            flexShrink: "0",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            window.open(
                              getImageUrl(banner.banner_image),
                              "_blank",
                            )
                          }
                        >
                          <img
                            src={getImageUrl(banner.banner_image)}
                            alt={`Banner ${banner.id}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.parentElement.innerHTML = `
                                <div style="
                                  width: 100%;
                                  height: 100%;
                                  background: #f1f5f9;
                                  display: flex;
                                  align-items: center;
                                  justify-content: center;
                                  color: #94a3b8;
                                ">
                                  <FiImage size={24} />
                                </div>
                              `;
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          style={{
                            width: "120px",
                            height: "80px",
                            borderRadius: "8px",
                            background: "#f1f5f9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#94a3b8",
                            flexShrink: "0",
                          }}
                        >
                          <FiImage style={{ fontSize: "1.5rem" }} />
                        </div>
                      )}
                    </div>
                  </td>

                  <td
                    style={{
                      padding: "20px",
                      borderBottom: "1px solid #f1f5f9",
                      verticalAlign: "top",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        background: banner.project_id ? "#e0f2fe" : "#f3f4f6",
                        color: banner.project_id ? "#0369a1" : "#6b7280",
                        border: `1px solid ${banner.project_id ? "#bae6fd" : "#e5e7eb"}`,
                        maxWidth: "200px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {banner.project_id
                        ? getProjectName(banner.project_id)
                        : "No Project"}
                    </div>
                  </td>

                  <td
                    style={{
                      padding: "20px",
                      borderBottom: "1px solid #f1f5f9",
                      verticalAlign: "top",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "6px 12px",
                        borderRadius: "20px",
                        fontSize: "0.8rem",
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        background: getStatusColor(banner.status).background,
                        color: getStatusColor(banner.status).color,
                      }}
                    >
                      {banner.status === "active" ? (
                        <>
                          <FiEye style={{ fontSize: "0.8rem" }} />
                          Active
                        </>
                      ) : (
                        <>
                          <FiEyeOff style={{ fontSize: "0.8rem" }} />
                          Inactive
                        </>
                      )}
                    </div>
                  </td>

                  <td
                    style={{
                      padding: "20px",
                      borderBottom: "1px solid #f1f5f9",
                      verticalAlign: "top",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                      }}
                    >
                      <button
                        onClick={() => handleEdit(banner)}
                        style={{
                          background: "#3b82f6",
                          color: "white",
                          border: "none",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          transition: "all 0.3s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                        title="Edit"
                      >
                        <FiEdit />
                      </button>
                      <button
                        onClick={() =>
                          handleUpdateStatus(banner.id, banner.status)
                        }
                        style={{
                          background:
                            banner.status === "active" ? "#f59e0b" : "#10b981",
                          color: "white",
                          border: "none",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          transition: "all 0.3s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                        title={
                          banner.status === "active" ? "Deactivate" : "Activate"
                        }
                      >
                        {banner.status === "active" ? <FiEyeOff /> : <FiEye />}
                      </button>
                      <button
                        onClick={() => handleDeleteBanner(banner.id)}
                        style={{
                          background: "#ef4444",
                          color: "white",
                          border: "none",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          fontWeight: "600",
                          transition: "all 0.3s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: "5px",
                        }}
                        title="Delete"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination - Same as before */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            marginTop: "20px",
          }}
        >
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{
              padding: "10px 16px",
              border: "1px solid #e2e8f0",
              background: "white",
              borderRadius: "8px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              color: currentPage === 1 ? "#cbd5e1" : "#475569",
              opacity: currentPage === 1 ? 0.5 : 1,
            }}
          >
            Previous
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                style={{
                  padding: "10px 16px",
                  border: "1px solid #e2e8f0",
                  background: currentPage === pageNum ? "#667eea" : "white",
                  color: currentPage === pageNum ? "white" : "#475569",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: currentPage === pageNum ? "600" : "400",
                }}
              >
                {pageNum}
              </button>
            );
          })}

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            style={{
              padding: "10px 16px",
              border: "1px solid #e2e8f0",
              background: "white",
              borderRadius: "8px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
              color: currentPage === totalPages ? "#cbd5e1" : "#475569",
              opacity: currentPage === totalPages ? 0.5 : 1,
            }}
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Banner Modal - Updated with project selection */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: "1000",
            padding: "20px",
            backdropFilter: "blur(5px)",
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "20px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "90vh",
              overflow: "hidden",
              boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
              animation: "modalSlideIn 0.3s ease",
            }}
          >
            <div
              style={{
                padding: "25px 30px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
              }}
            >
              <h2
                style={{
                  margin: "0",
                  fontSize: "1.5rem",
                  fontWeight: "600",
                }}
              >
                {editingBanner ? "Edit Banner" : "Add New Banner"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  fontSize: "1.5rem",
                  color: "white",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "background 0.3s ease",
                }}
                onMouseOver={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.3)";
                }}
                onMouseOut={(e) => {
                  e.target.style.background = "rgba(255, 255, 255, 0.2)";
                }}
              >
                &times;
              </button>
            </div>

            <form
              onSubmit={editingBanner ? handleUpdateBanner : handleAddBanner}
              style={{
                padding: "30px",
                maxHeight: "calc(90vh - 100px)",
                overflowY: "auto",
              }}
            >
              {/* Project Selection Dropdown */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "12px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "1rem",
                  }}
                >
                  Associated Project (Optional)
                </label>

                <div style={{ position: "relative" }}>
                  <button
                    type="button"
                    onClick={() => setShowProjectDropdown(!showProjectDropdown)}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "12px",
                      background: "white",
                      textAlign: "left",
                      fontSize: "1rem",
                      color: formData.project_id ? "#1e293b" : "#94a3b8",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      transition: "all 0.3s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = "#667eea";
                      e.target.style.boxShadow =
                        "0 0 0 3px rgba(102, 126, 234, 0.1)";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = "#e2e8f0";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    <span>
                      {formData.project_id
                        ? getProjectName(formData.project_id)
                        : "Select a project (optional)"}
                    </span>
                    <FiChevronDown
                      style={{
                        transition: "transform 0.3s ease",
                        transform: showProjectDropdown
                          ? "rotate(180deg)"
                          : "rotate(0)",
                      }}
                    />
                  </button>

                  {showProjectDropdown && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "0",
                        right: "0",
                        background: "white",
                        border: "2px solid #e2e8f0",
                        borderRadius: "12px",
                        marginTop: "4px",
                        maxHeight: "300px",
                        overflowY: "auto",
                        zIndex: "1001",
                        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => selectProject("")}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            textAlign: "left",
                            background: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "1rem",
                            color: "#64748b",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            transition: "background 0.3s ease",
                          }}
                          onMouseOver={(e) => {
                            e.target.style.background = "#f8fafc";
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = "white";
                          }}
                        >
                          <span>No Project</span>
                          {!formData.project_id && <FiCheck />}
                        </button>

                        {projects.map((project) => (
                          <button
                            key={project.pid}
                            type="button"
                            onClick={() => selectProject(project.pid)}
                            style={{
                              width: "100%",
                              padding: "12px 16px",
                              textAlign: "left",
                              background: "white",
                              border: "none",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontSize: "1rem",
                              color: "#1e293b",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              transition: "background 0.3s ease",
                            }}
                            onMouseOver={(e) => {
                              e.target.style.background = "#f8fafc";
                            }}
                            onMouseOut={(e) => {
                              e.target.style.background = "white";
                            }}
                          >
                            <span>{project.project_name}</span>
                            {formData.project_id === project.pid && <FiCheck />}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Banner Image Upload */}
              <div style={{ marginBottom: "25px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "12px",
                    fontWeight: "600",
                    color: "#374151",
                    fontSize: "1rem",
                  }}
                >
                  Banner Image {!editingBanner && "*"}
                </label>

                <div
                  style={{
                    border: "2px dashed #e2e8f0",
                    borderRadius: "12px",
                    padding: "30px",
                    textAlign: "center",
                    background: imagePreview ? "transparent" : "#f9f9f9",
                    transition: "all 0.3s ease",
                  }}
                >
                  {imagePreview ? (
                    <div>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "200px",
                          borderRadius: "10px",
                          marginBottom: "20px",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          justifyContent: "center",
                        }}
                      >
                        <button
                          type="button"
                          onClick={removeImage}
                          style={{
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FiX />
                          Remove Image
                        </button>
                        <label
                          style={{
                            background: "#3b82f6",
                            color: "white",
                            border: "none",
                            padding: "10px 20px",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "0.9rem",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <FiUpload />
                          Change Image
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            style={{ display: "none" }}
                          />
                        </label>
                      </div>
                    </div>
                  ) : editingBanner && editingBanner.banner_image ? (
                    <div>
                      <div
                        style={{
                          position: "relative",
                          width: "100%",
                          maxHeight: "200px",
                          marginBottom: "20px",
                        }}
                      >
                        <img
                          src={getImageUrl(editingBanner.banner_image)}
                          alt="Current Banner"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "200px",
                            borderRadius: "10px",
                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            background: "rgba(0, 0, 0, 0.7)",
                            color: "white",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "0.75rem",
                          }}
                        >
                          Current Image
                        </div>
                      </div>
                      <label
                        style={{
                          background: "#3b82f6",
                          color: "white",
                          border: "none",
                          padding: "12px 24px",
                          borderRadius: "10px",
                          cursor: "pointer",
                          fontSize: "0.95rem",
                          fontWeight: "600",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          transition: "all 0.3s ease",
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 8px 25px rgba(102, 126, 234, 0.4)";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        <FiUpload />
                        Change Image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                        />
                      </label>
                    </div>
                  ) : (
                    <div>
                      <FiUpload
                        style={{
                          fontSize: "3rem",
                          color: "#cbd5e1",
                          marginBottom: "15px",
                        }}
                      />
                      <p
                        style={{
                          color: "#64748b",
                          marginBottom: "15px",
                          fontSize: "0.95rem",
                        }}
                      >
                        Drag & drop your banner image here, or click to browse
                      </p>
                      <label
                        style={{
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          color: "white",
                          border: "none",
                          padding: "12px 24px",
                          borderRadius: "10px",
                          cursor: "pointer",
                          fontSize: "0.95rem",
                          fontWeight: "600",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          transition: "all 0.3s ease",
                        }}
                        onMouseOver={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 8px 25px rgba(102, 126, 234, 0.4)";
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        <FiUpload />
                        Select Image
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileChange}
                          style={{ display: "none" }}
                          required={!editingBanner}
                        />
                      </label>
                      <p
                        style={{
                          color: "#94a3b8",
                          fontSize: "0.85rem",
                          marginTop: "15px",
                        }}
                      >
                        Supported formats: JPEG, PNG, GIF (Max 5MB)
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Field (for edit mode) */}
              {editingBanner && (
                <div style={{ marginBottom: "25px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "12px",
                      fontWeight: "600",
                      color: "#374151",
                      fontSize: "1rem",
                    }}
                  >
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e2e8f0",
                      borderRadius: "12px",
                      fontSize: "1rem",
                      background: "white",
                      cursor: "pointer",
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}

              <div
                style={{
                  paddingTop: "25px",
                  borderTop: "1px solid #e2e8f0",
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "15px",
                  marginTop: "20px",
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  disabled={uploading}
                  style={{
                    background: "transparent",
                    border: "2px solid #e2e8f0",
                    padding: "12px 25px",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    color: "#64748b",
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    if (!uploading) {
                      e.target.style.background = "#f8fafc";
                      e.target.style.borderColor = "#cbd5e1";
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.borderColor = "#e2e8f0";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    uploading || (!editingBanner && !formData.banner_image)
                  }
                  style={{
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    padding: "12px 30px",
                    borderRadius: "10px",
                    cursor: uploading ? "not-allowed" : "pointer",
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    opacity:
                      uploading || (!editingBanner && !formData.banner_image)
                        ? 0.7
                        : 1,
                    transition: "all 0.3s ease",
                  }}
                  onMouseOver={(e) => {
                    if (
                      !uploading &&
                      (!editingBanner ? formData.banner_image : true)
                    ) {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 8px 25px rgba(102, 126, 234, 0.4)";
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  {uploading ? (
                    <>
                      <div
                        style={{
                          display: "inline-block",
                          width: "16px",
                          height: "16px",
                          border: "2px solid rgba(255, 255, 255, 0.3)",
                          borderTop: "2px solid white",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          marginRight: "8px",
                          verticalAlign: "middle",
                        }}
                      ></div>
                      {editingBanner ? "Updating..." : "Uploading..."}
                    </>
                  ) : editingBanner ? (
                    "Update Banner"
                  ) : (
                    "Upload Banner"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default BannerManagement;
